/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, PlaySquare } from 'lucide-react';
import api from '@services/api';

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const REQUEST_LIMIT = 8;
const MAX_COURSE_ROWS = 5;
const MAX_MODULE_ROWS = 3;

interface CourseHit {
  id: string;
  title: string;
  snippet: string;
  category_name?: string | null;
  status?: string;
}

interface ModuleHit {
  id: string;
  course_id: string;
  title: string;
  course_title: string;
  snippet: string;
}

export interface SearchOption {
  id: string;
  kind: 'course' | 'module' | 'see-all';
  title: string;
  subtitle?: string;
  snippet?: string;
  courseId?: string;
}

/**
 * Snippets arrive as text with <mark>…</mark> around matches. Descriptions and
 * transcripts are user-authored, so the string must never reach
 * dangerouslySetInnerHTML — instead split on the mark boundaries and render
 * alternating text/<mark> nodes, which neutralizes any other markup as text.
 */
export function renderSnippet(snippet: string): React.ReactNode[] {
  return snippet
    .split(/<\/?mark>/)
    .map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part));
}

function buildOptions(query: string, courses: CourseHit[], modules: ModuleHit[]): SearchOption[] {
  const options: SearchOption[] = courses.slice(0, MAX_COURSE_ROWS).map((c) => ({
    id: `course-${c.id}`,
    kind: 'course' as const,
    title: c.title,
    subtitle: c.category_name ?? undefined,
    snippet: c.snippet,
    courseId: c.id,
  }));
  for (const m of modules.slice(0, MAX_MODULE_ROWS)) {
    options.push({
      id: `module-${m.id}`,
      kind: 'module',
      title: m.title,
      subtitle: m.course_title,
      snippet: m.snippet,
      courseId: m.course_id,
    });
  }
  options.push({ id: 'see-all', kind: 'see-all', title: `See all results for “${query}”` });
  return options;
}

/** Debounced, abortable typeahead state against GET /courses/search. */
function useTypeahead(query: string) {
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [settledQuery, setSettledQuery] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setSettledQuery('');
      setSearching(false);
      setFailed(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearching(true);
      api
        .get('/courses/search', {
          params: { q, limit: REQUEST_LIMIT },
          signal: controller.signal,
        })
        .then((res) => {
          const { courses = [], modules = [] } = res.data?.data ?? {};
          setOptions(buildOptions(q, courses, modules));
          setSettledQuery(q);
          setSearching(false);
          setFailed(false);
        })
        .catch((err: unknown) => {
          // An abort means a newer query superseded this one — stay quiet.
          if ((err as { code?: string })?.code === 'ERR_CANCELED') return;
          // A failed request is NOT an empty result set: saying "no results"
          // would tell the user their term matches nothing when in fact we
          // never found out. (The api interceptor has already logged it.)
          setOptions([]);
          setSettledQuery(q);
          setSearching(false);
          setFailed(true);
        });
    }, DEBOUNCE_MS);
    // Cleanup cancels both the pending debounce tick and any in-flight request,
    // so a stale response can never overwrite a newer query's results.
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  return { options, searching, settledQuery, failed };
}

const SEARCH_FAILED_MESSAGE = 'Search is unavailable right now. Try again in a moment.';

function statusMessage(
  searching: boolean,
  settled: boolean,
  resultCount: number,
  failed: boolean
): string {
  if (searching) return 'Searching…';
  if (!settled) return '';
  if (failed) return SEARCH_FAILED_MESSAGE;
  return resultCount > 0 ? `${resultCount} results` : 'No results';
}

/**
 * The row shown when a settled search lists nothing. A failed request also has
 * zero results, so this is where the two are told apart: "we could not search"
 * must never read as "your term matches nothing".
 */
function EmptyRow({ failed, query }: { failed: boolean; query: string }) {
  return failed ? (
    <li className="px-4 py-2.5 text-sm text-destructive" role="presentation">
      {SEARCH_FAILED_MESSAGE}
    </li>
  ) : (
    <li className="px-4 py-2.5 text-sm text-muted-foreground" role="presentation">
      No courses match “{query}”
    </li>
  );
}

function OptionRow({
  option,
  active,
  onSelect,
}: {
  option: SearchOption;
  active: boolean;
  onSelect: (o: SearchOption) => void;
}) {
  const Icon = option.kind === 'module' ? PlaySquare : option.kind === 'course' ? BookOpen : Search;
  return (
    <li
      id={`header-search-${option.id}`}
      role="option"
      aria-selected={active}
      className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer text-sm ${active ? 'bg-surface-hover' : 'hover:bg-surface-hover'}`}
      // mousedown, not click: the input's blur/click-outside handling must not
      // dismiss the listbox before the selection lands.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect(option);
      }}
    >
      <Icon size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="font-medium text-foreground truncate">{option.title}</div>
        {option.subtitle && (
          <div className="text-xs text-muted-foreground truncate">{option.subtitle}</div>
        )}
        {option.snippet && (
          <div className="text-xs text-muted-foreground truncate [&_mark]:bg-yellow-200 [&_mark]:rounded-sm">
            {renderSnippet(option.snippet)}
          </div>
        )}
      </div>
    </li>
  );
}

export default function HeaderSearch({ isLearnerView }: { isLearnerView: boolean }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const { options, searching, settledQuery, failed } = useTypeahead(query);
  const settled = settledQuery === query.trim() && settledQuery.length >= MIN_QUERY_LENGTH;
  const showList = open && settled;
  const resultCount = Math.max(options.length - 1, 0); // "see all" row is not a result

  useEffect(() => {
    setActiveIndex(-1);
  }, [settledQuery]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const goToList = () => {
    const q = query.trim();
    if (!q) return;
    setOpen(false);
    navigate({
      pathname: isLearnerView ? '/learn' : '/courses',
      search: new URLSearchParams({ q }).toString(),
    });
  };

  const selectOption = (option: SearchOption) => {
    if (option.kind === 'see-all') {
      goToList();
      return;
    }
    setOpen(false);
    const base = isLearnerView ? '/learn/courses' : '/courses';
    navigate(`${base}/${option.courseId}`);
  };

  const moveActive = (key: string) => {
    if (!showList || options.length === 0) return;
    const delta = key === 'ArrowDown' ? 1 : -1;
    setActiveIndex((i) => (i + delta + options.length) % options.length);
  };

  const chooseActive = () => {
    const active = showList && activeIndex >= 0 ? options[activeIndex] : undefined;
    if (active) selectOption(active);
    else goToList();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (open) setOpen(false);
      else setQuery('');
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(e.key);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      chooseActive();
    }
  };

  return (
    <div ref={rootRef} className="relative flex-1 max-w-[420px]">
      <Search
        size={16}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none"
      />
      <input
        type="text"
        role="combobox"
        aria-label="Search courses"
        aria-expanded={showList}
        aria-controls="header-search-listbox"
        aria-autocomplete="list"
        aria-activedescendant={
          showList && activeIndex >= 0 ? `header-search-${options[activeIndex]?.id}` : undefined
        }
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search courses..."
        className="w-full h-10 pl-10 pr-3 bg-input-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary/40 transition-all"
      />
      <span role="status" aria-live="polite" className="sr-only">
        {statusMessage(searching, settled, resultCount, failed)}
      </span>
      {/* The listbox stays mounted so aria-controls always resolves. */}
      <ul
        id="header-search-listbox"
        role="listbox"
        aria-label="Search results"
        hidden={!showList}
        className="absolute left-0 right-0 top-full mt-2 py-1 bg-white border border-surface-border rounded-2xl shadow-lg overflow-hidden z-50"
      >
        {resultCount === 0 && settled && <EmptyRow failed={failed} query={query.trim()} />}
        {showList &&
          options.map((option, i) => (
            <OptionRow
              key={option.id}
              option={option}
              active={i === activeIndex}
              onSelect={selectOption}
            />
          ))}
      </ul>
    </div>
  );
}
